import React, { useState } from "react";
import { ApolloError, gql, useMutation, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import * as O from "fp-ts/Option";
import { pipe } from "fp-ts/function";
import CopyToClipboard from "react-copy-to-clipboard";
import { Redirect, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { CopyIcon } from "src/assets/icons/copy_icon";
import { Button } from "src/components/button/button";
import { CheckBoxGroup } from "src/components/checkbox/checkbox_group";
import { Error } from "src/components/error/error";
import { Input } from "src/components/input/input";
import { Loading } from "src/components/loading/loading";
import { Modal } from "src/components/modal/modal";
import { ProgressCircleGroup } from "src/components/progress_circle/progress_circle_group";
import { ProgressCircleGroupCombined } from "../../components/progress_circle/progress_circle_group_combined";
import { Separator } from "src/components/separator/separator";
import { StepIndicator } from "src/components/step_indicator/step_indicator";
import { Toggle } from "src/components/toggle/toggle";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { CallCloudCodeInput, CallCloudCodePayload, CloudCodeFunction, Report } from "src/generated/types";
import { parseError } from "src/utils/parse_error";
import { Routes } from "src/utils/routes";
import { ApolloQueryData, PROMOTION_QUERY } from "src/utils/promotion_query";

import { Emails } from "./email_contents";
import { SurveyStatusHelp } from "./survey_status_help";

import "./promotion.scss";

/*
 * GraphQL
 */

const LINK_DELIVERED_MUTATION = gql`
    mutation UpdatePromotionProgress($input: CallCloudCodeInput!) {
        callCloudCode(input: $input) {
            result
        }
    }
`;

const GENERATE_REPORT_MUTATION = gql`
    mutation GenerateReport($input: CallCloudCodeInput!) {
        callCloudCode(input: $input) {
            result
        }
    }
`;

const GET_CURRENT_REPORT = gql`
    query GetCurrentReport {
        viewer {
            user {
                id
                school {
                    id
                    currentReport {
                        id
                        promotionProgress
                        percentCompleted
                        status
                        submissionProgress
                        surveysCompleted
                        constituentGroups
                    }
                    currentReports {
                        edges {
                            node {
                                id
                                type
                                promotionProgress
                                percentCompleted
                                status
                                submissionProgress
                                surveysCompleted
                                constituentGroups
                            }
                        }
                    }
                }
            }
        }
    }
`;

/*
 * Types
 */

interface ApolloMutationVars {
    input: CallCloudCodeInput;
}

interface ApolloMutationData {
    updateReport: CallCloudCodePayload;
}

/*
 * Main
 */

const lookup: Record<ConstituencyGroup, string> = {
    administrators: "Administrator",
    alumni: "Alumni",
    boardMembers: "Board Member",
    parents: "Parent",
    students: "Student",
    supportStaff: "Support Staff",
    teachers: "Teacher",
};

const modalContent: Record<ConstituencyGroup, string> = {
    administrators:
        "Questions are specific to each stakeholder group. Administrators must use this specific link to complete the assessment.",
    alumni: "Questions are specific to each stakeholder group. Alumni must use this specific link to complete the assessment",
    boardMembers:
        "Questions are specific to each stakeholder group. Board Members must use this specific link to complete the assessment.",
    parents:
        "Questions are specific to each stakeholder group. Parents must use this specific link to complete the assessment.",
    students:
        "Questions are specific to each stakeholder group. Students must use this specific link to complete the assessment.",
    supportStaff:
        "Questions are specific to each stakeholder group. All Support Staff must use this specific link to complete the assessment.",
    teachers:
        "Questions are specific to each stakeholder group. Teachers must use this specific link to complete the assessment.",
};

const requiredSteps = ["accountInformation", "needsConstituentGroups"];

export const PromotionPage = (): React.ReactElement => {
    const studentEmailsRef = React.createRef<HTMLDivElement>();
    const teacherEmailsRef = React.createRef<HTMLDivElement>();
    const administratorEmailsRef = React.createRef<HTMLDivElement>();
    const supportStaffEmailsRef = React.createRef<HTMLDivElement>();
    const parentEmailsRef = React.createRef<HTMLDivElement>();
    const alumniEmailsRef = React.createRef<HTMLDivElement>();
    const boardMembersEmailsRef = React.createRef<HTMLDivElement>();
    const topOfPageRef = React.createRef<HTMLDivElement>();
    const history = useHistory();
    const [updatePromotionProgress] = useMutation<ApolloMutationData, ApolloMutationVars>(LINK_DELIVERED_MUTATION, {
        refetchQueries: [{ query: GET_CURRENT_REPORT }],
    });
    const [generateReport] = useMutation(GENERATE_REPORT_MUTATION);
    const { data, error, loading } = useQuery<ApolloQueryData, Empty>(PROMOTION_QUERY);
    const [isAdministratorsModalOpen, setAdministratorsModalOpen] = useState(false);
    const [isAlumniModalOpen, setAlumniModalOpen] = useState(false);
    const [isBoardMembersModalOpen, setBoardMembersModalOpen] = useState(false);
    const [isParentsModalOpen, setParentsModalOpen] = useState(false);
    const [isStudentModalOpen, setStudentModalOpen] = useState(false);
    const [isSupportStaffModalOpen, setSupportStaffModalOpen] = useState(false);
    const [isTeachersModalOpen, setTeachersModalOpen] = useState(false);
    const [isEarlyReportModalOpen, setEarlyReportModalOpen] = useState(false);
    const [isSurveyStatusModalOpen, setSurveyStatusModalOpen] = useState(false);

    if (!loading && !error && requiredSteps.includes(data?.viewer.user.school?.currentReport?.status || "")) {
        return (
            <Redirect
                to={{
                    pathname: "/",
                }}
            />
        );
    }

    const generate = async (schoolId: string | undefined): Promise<void> => {
        if (!schoolId) {
            toast.error("No school ID found, please contact an admin.");
            return;
        }
        try {
            await generateReport({
                variables: {
                    input: {
                        functionName: CloudCodeFunction.GenerateReports,
                        params: {
                            schoolId,
                        },
                    },
                },
            });
            history.push(Routes.report.home);
        } catch (e: any) {
            toast.error(parseError(e));
        }
    };

    const isModalOpen = (constituentGroup: string): boolean => {
        switch (constituentGroup) {
            case "administrators":
                return isAdministratorsModalOpen;
            case "alumni":
                return isAlumniModalOpen;
            case "boardMembers":
                return isBoardMembersModalOpen;
            case "parents":
                return isParentsModalOpen;
            case "students":
                return isStudentModalOpen;
            case "supportStaff":
                return isSupportStaffModalOpen;
            case "teachers":
                return isTeachersModalOpen;
            default:
                return false;
        }
    };

    const getModalOpenDispatch = (constituentGroup: string): React.Dispatch<React.SetStateAction<boolean>> => {
        switch (constituentGroup) {
            case "administrators":
                return setAdministratorsModalOpen;
            case "alumni":
                return setAlumniModalOpen;
            case "boardMembers":
                return setBoardMembersModalOpen;
            case "parents":
                return setParentsModalOpen;
            case "students":
                return setStudentModalOpen;
            case "supportStaff":
                return setSupportStaffModalOpen;
            case "teachers":
                return setTeachersModalOpen;
            default:
                return setAdministratorsModalOpen;
        }
    };

    const setModalOpen = (open: boolean, constituentGroup: string): void => {
        getModalOpenDispatch(constituentGroup)(open);
    };

    if (loading) return <Loading />;
    if (error) return <Error />;

    const fsciReport = data?.viewer.user.school?.currentReports?.edges?.find(
        (edge) => edge?.node?.type === "fsci",
    )?.node;
    const ffiReport = data?.viewer.user.school?.currentReports?.edges?.find((edge) => edge?.node?.type === "ffi")?.node;
    const isCombinedReport = fsciReport && ffiReport;

    const renderJumpToLinks = () => {
        return (
            <div className="jump-to-link-wrap">
                <span className="first-text">Jump to: </span>
                <button
                    className="jump-to-link"
                    onClick={(): void => {
                        studentEmailsRef?.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }}
                >
                    Student Emails
                </button>
                <span className="divider">|</span>
                <button
                    className="jump-to-link"
                    onClick={(): void => {
                        teacherEmailsRef?.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }}
                >
                    Teacher Emails
                </button>
                <span className="divider">|</span>
                <button
                    className="jump-to-link"
                    onClick={(): void => {
                        administratorEmailsRef?.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }}
                >
                    Administrator Emails
                </button>
                <span className="divider">|</span>
                <button
                    className="jump-to-link"
                    onClick={(): void => {
                        supportStaffEmailsRef?.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }}
                >
                    Support Staff Emails
                </button>
                <span className="divider">|</span>
                <button
                    className="jump-to-link"
                    onClick={(): void => {
                        parentEmailsRef?.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }}
                >
                    Parent Emails
                </button>
                <span className="divider">|</span>
                <button
                    className="jump-to-link"
                    onClick={(): void => {
                        alumniEmailsRef?.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }}
                >
                    Alumni Emails
                </button>
                <span className="divider">|</span>
                <button
                    className="jump-to-link"
                    onClick={(): void => {
                        boardMembersEmailsRef?.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }}
                >
                    Board Member Emails
                </button>
            </div>
        );
    };

    const _renderSurveyLinkRow = (constituencyGroup: ConstituencyGroup, currentReport: Report) => {
        return (
            <div className="promotion-top-line">
                <div className="link-toggle-wrap">
                    <span className="link-delivered-text">Link Delivered</span>
                    <Toggle
                        checked={currentReport.promotionProgress[constituencyGroup]}
                        data-testid={`${constituencyGroup}_email_toggle`}
                        name={`${constituencyGroup}_email_toggle`}
                        onChange={(): void => {
                            updatePromotionProgress({
                                variables: {
                                    input: {
                                        functionName: CloudCodeFunction.UpdateConstituentGroupPromotionStatus,
                                        params: {
                                            constituentGroup: constituencyGroup,
                                            schoolId: data?.viewer.user.school?.id,
                                            status: !currentReport.promotionProgress[constituencyGroup],
                                        },
                                    },
                                },
                            })
                                .then(() => {
                                    toast.success("Updated!");
                                })
                                .catch((e: ApolloError) => {
                                    toast.error(parseError(e));
                                });
                        }}
                    />
                </div>
                <div className="input-outer-wrap">
                    <Formik
                        initialValues={{
                            [constituencyGroup]: currentReport.surveyUrls[constituencyGroup],
                        }}
                        onSubmit={(_, { setSubmitting }): void => {
                            setSubmitting(false);
                        }}
                    >
                        {() => (
                            <Form>
                                <Input
                                    id={`${constituencyGroup}-survey-link-input`}
                                    label={`${lookup[constituencyGroup]} Survey Link`}
                                    name={constituencyGroup}
                                    renderLabelMeta={() => (
                                        <div className="modal-link-container">
                                            <span
                                                className="whats-this-text"
                                                onClick={(): void => {
                                                    setModalOpen(true, `${constituencyGroup}`);
                                                }}
                                            >
                                                What's this?
                                            </span>
                                            <Modal
                                                isOpen={isModalOpen(`${constituencyGroup}`)}
                                                setOpen={getModalOpenDispatch(`${constituencyGroup}`)}
                                                title={`${lookup[constituencyGroup]} Survey Link`}
                                            >
                                                <Body> {`${modalContent[constituencyGroup]}`}</Body>
                                            </Modal>
                                        </div>
                                    )}
                                    type="text"
                                />
                                <CopyToClipboard
                                    onCopy={(): void => {
                                        toast.success("Survey link copied to clipboard.");
                                    }}
                                    text={currentReport.surveyUrls[constituencyGroup]}
                                >
                                    <Button variation="icon">
                                        <CopyIcon />
                                        Copy
                                    </Button>
                                </CopyToClipboard>
                            </Form>
                        )}
                    </Formik>
                </div>
                <div className="clear" />
            </div>
        );
    };

    const _renderColumn = (columnHeader: string, content: string) => {
        return (
            <div className="inner-email-copy-wrap">
                <div className="input-wrap">
                    <label>{columnHeader}</label>
                    <div className="textarea-wrap">
                        <textarea readOnly value={content} />
                    </div>
                </div>
                <CopyToClipboard
                    onCopy={(): void => {
                        toast.success("Email text copied to clipboard.");
                    }}
                    text={content}
                >
                    <Button variation="icon">
                        <CopyIcon />
                        Copy Email Text
                    </Button>
                </CopyToClipboard>
            </div>
        );
    };

    const renderSurveySection = (constituencyGroup: ConstituencyGroup, currentReport: Report) => {
        return (
            <>
                <SectionTitle>{`${lookup[constituencyGroup]} Survey`}</SectionTitle>
                <div className="email-promotion-wrapper">
                    {_renderSurveyLinkRow(constituencyGroup, currentReport)}
                    <div className="main-email-copy-wrap">
                        {_renderColumn(
                            `${lookup[constituencyGroup]} First Email`,
                            Emails(constituencyGroup, currentReport.surveyUrls[constituencyGroup]).initial,
                        )}
                        {_renderColumn(
                            `${lookup[constituencyGroup]} Follow-Up Email`,
                            Emails(constituencyGroup, currentReport.surveyUrls[constituencyGroup]).followUp,
                        )}
                        {_renderColumn(
                            `${lookup[constituencyGroup]} Final Email`,
                            Emails(constituencyGroup, currentReport.surveyUrls[constituencyGroup]).final,
                        )}
                    </div>
                </div>
            </>
        );
    };

    return pipe(
        O.fromNullable(data?.viewer.user.school?.currentReport),
        O.fold(
            () => (
                <div className="main-page-wrap short">
                    <div className="center">
                        <div className="page-section">
                            <PageTitle>Survey Status</PageTitle>
                        </div>
                        <Separator />
                        <Error message="There is a problem with your account. Please contact an Admin so they can resolve the issue." />
                    </div>
                </div>
            ),
            (currentReport) => (
                <div className="main-page-wrap short" ref={topOfPageRef}>
                    <div className="center">
                        <div className="page-section">
                            <StepIndicator report={currentReport} />
                            <Separator />
                        </div>
                        <div className="page-section">
                            <div className="title-wrap">
                                <PageTitle>Survey Status</PageTitle>
                                <span
                                    className="whats-this-text"
                                    onClick={(): void => {
                                        setSurveyStatusModalOpen(true);
                                    }}
                                >
                                    What's this?
                                </span>
                                <Modal
                                    isOpen={isSurveyStatusModalOpen}
                                    setOpen={setSurveyStatusModalOpen}
                                    title="Survey Status"
                                >
                                    <SurveyStatusHelp />
                                </Modal>
                            </div>
                            <div className="progress-circle-wrapper flex-wrapper bar-under">
                                {isCombinedReport ? (
                                    <ProgressCircleGroupCombined
                                        constituentGroups={{
                                            fsci: fsciReport.constituentGroups,
                                            ffi: ffiReport.constituentGroups,
                                        }}
                                        submissionProgress={{
                                            fsci: fsciReport.submissionProgress,
                                            ffi: ffiReport.submissionProgress,
                                        }}
                                        surveysCompleted={{
                                            fsci: fsciReport.surveysCompleted,
                                            ffi: ffiReport.surveysCompleted,
                                        }}
                                    />
                                ) : (
                                    <ProgressCircleGroup
                                        constituentGroups={currentReport.constituentGroups}
                                        submissionProgress={currentReport.submissionProgress}
                                        surveysCompleted={currentReport.surveysCompleted}
                                    />
                                )}
                            </div>
                            {(currentReport.status as ReportStatus) === "collectingData" ? (
                                <div className="finalize-early-link-wrap">
                                    <span
                                        onClick={(): void => {
                                            setEarlyReportModalOpen(true);
                                        }}
                                    >
                                        I Want to Finalize My Report Early
                                    </span>
                                    <Modal
                                        isOpen={isEarlyReportModalOpen}
                                        setOpen={setEarlyReportModalOpen}
                                        title="This action cannot be undone"
                                    >
                                        <Body>
                                            You are able to run your report early, but it is important to note that you
                                            may only run your report one time. Running it early means that the results
                                            will not comprehensively represent the perspectives of your audience. For
                                            complete and/or statistically valid results, you must wait until the survey
                                            responses have reached their minimum thresholds. If you wish to proceed,
                                            select Run Report Early.
                                        </Body>
                                        <Button
                                            onClick={() => {
                                                generate(data?.viewer.user.school?.id);
                                                setEarlyReportModalOpen(false);
                                            }}
                                            variation="secondary"
                                        >
                                            I understand, run the report early
                                        </Button>
                                    </Modal>
                                </div>
                            ) : null}
                            {renderJumpToLinks()}
                            <Separator />
                        </div>
                        <div className="page-section">
                            <SectionTitle>Promotion Progress</SectionTitle>
                            <div className="checkbox-wrapper flex-wrapper">
                                <CheckBoxGroup promotionProgress={currentReport.promotionProgress} />
                            </div>
                            <Separator />
                        </div>
                        <div className="page-section group-promotion-section" ref={studentEmailsRef}>
                            <SectionTitle>{`${lookup["students"]} Survey`}</SectionTitle>
                            <div className="email-promotion-wrapper">
                                {_renderSurveyLinkRow("students", currentReport)}
                                <a
                                    href="https://fsci-files.s3.amazonaws.com/FSCI_FFI_Student_Instructions.pdf"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                    }}
                                    target="_blank"
                                >
                                    <Button type="button" variation="icon">
                                        Download Instructions
                                    </Button>
                                </a>
                            </div>
                        </div>
                        <div className="page-section group-promotion-section" ref={teacherEmailsRef}>
                            {renderSurveySection("teachers", currentReport)}
                        </div>
                        <div className="page-section group-promotion-section" ref={administratorEmailsRef}>
                            {renderSurveySection("administrators", currentReport)}
                        </div>
                        <div className="page-section group-promotion-section" ref={supportStaffEmailsRef}>
                            {renderSurveySection("supportStaff", currentReport)}
                        </div>
                        <div className="page-section group-promotion-section" ref={parentEmailsRef}>
                            {renderSurveySection("parents", currentReport)}
                        </div>
                        <div className="page-section group-promotion-section" ref={alumniEmailsRef}>
                            {renderSurveySection("alumni", currentReport)}
                        </div>
                        <div className="page-section group-promotion-section" ref={boardMembersEmailsRef}>
                            {renderSurveySection("boardMembers", currentReport)}
                        </div>
                        <div className="back-to-top-link-wrap">
                            <span
                                className="back-to-top-link"
                                onClick={(): void => {
                                    topOfPageRef?.current?.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                    });
                                }}
                            >
                                Back to Top of Page
                            </span>
                        </div>
                        {renderJumpToLinks()}
                    </div>
                </div>
            ),
        ),
    );
};
